import React, { useState } from 'react';
import { Table, Card, Button, Popconfirm, Tag, Space, DatePicker, Select } from 'antd';
import { DeleteOutlined, UnorderedListOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useHealthStore } from '@/stores/useHealthStore';
import type { BloodPressureRecord, BloodSugarRecord } from '@/types';

const { RangePicker } = DatePicker;

const DataList: React.FC = () => {
  const { bpRecords, bsRecords, deleteRecord } = useHealthStore();
  const [filterType, setFilterType] = useState<'all' | 'bp' | 'bs'>('all');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  // Filter records
  const getFilteredRecords = () => {
    let records: (BloodPressureRecord | BloodSugarRecord & { recordType: 'bp' | 'bs' })[] = [];

    if (filterType === 'all' || filterType === 'bp') {
      records = [...records, ...bpRecords.map(r => ({ ...r, recordType: 'bp' as const }))];
    }
    if (filterType === 'all' || filterType === 'bs') {
      records = [...records, ...bsRecords.map(r => ({ ...r, recordType: 'bs' as const }))];
    }

    if (dateRange && dateRange[0] && dateRange[1]) {
      const start = dateRange[0].format('YYYY-MM-DD');
      const end = dateRange[1].format('YYYY-MM-DD');
      records = records.filter(r => r.date >= start && r.date <= end);
    }

    return records.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  };

  const filteredRecords = getFilteredRecords();

  const columns = [
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date',
      sorter: (a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()
    },
    {
      title: '类型',
      key: 'type',
      render: (_: any, record: any) => (
        record.recordType === 'bp' ? (
          <Tag color="red">血压</Tag>
        ) : (
          <Tag color="blue">血糖</Tag>
        )
      )
    },
    {
      title: '数值',
      key: 'value',
      render: (_: any, record: any) => {
        if (record.recordType === 'bp') {
          return (
            <span>
              <strong style={{ color: '#ff4d4f' }}>
                {record.systolic}/{record.diastolic}
              </strong>
              {' '}mmHg
              {record.heartRate && (
                <span style={{ color: '#888', marginLeft: 8 }}>
                  心率 {record.heartRate}
                </span>
              )}
            </span>
          );
        } else {
          return (
            <span>
              <strong style={{ color: '#1890ff' }}>
                {record.value}
              </strong>
              {' '}mmol/L
              <Tag style={{ marginLeft: 8, fontSize: '12px' }}>
                {record.type === 'fasting' ? '空腹' : 
                 record.type === 'post_meal_1h' ? '餐后1h' :
                 record.type === 'post_meal_2h' ? '餐后2h' : '随机'}
              </Tag>
            </span>
          );
        }
      }
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: any) => (
        <Popconfirm
          title="确认删除"
          description="确定要删除这条记录吗？"
          onConfirm={() => record.recordId && deleteRecord(record.recordId)}
          okText="确定"
          cancelText="取消"
        >
          <Button type="link" danger icon={<DeleteOutlined />}>
            删除
          </Button>
        </Popconfirm>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ marginBottom: 24 }}>
        <UnorderedListOutlined style={{ marginRight: 8 }} />
        数据列表
      </h1>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            value={filterType}
            onChange={setFilterType}
            style={{ width: 120 }}
            options={[
              { label: '全部', value: 'all' },
              { label: '血压', value: 'bp' },
              { label: '血糖', value: 'bs' }
            ]}
          />
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            placeholder={['开始日期', '结束日期']}
          />
          <Button onClick={() => { setFilterType('all'); setDateRange(null); }}>
            重置筛选
          </Button>
        </Space>
      </Card>

      <Card>
        <Table
          dataSource={filteredRecords}
          columns={columns}
          rowKey={(record: any) => `${record.recordType}-${record.id}`}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: '暂无数据' }}
        />
      </Card>
    </div>
  );
};

export default DataList;
